(() => {
  const MODULE_SELECTOR = "[data-hc-routine]";

  class HCCompleteRoutine {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId || "";
      this.freeShippingThreshold = Number(root.dataset.freeShippingThreshold || 0);
      this.cartUrl = root.dataset.cartUrl || "/cart";

      this.items = Array.from(root.querySelectorAll("[data-routine-product]"));
      this.checkboxes = Array.from(root.querySelectorAll("[data-routine-checkbox]"));
      this.noToggleElements = Array.from(root.querySelectorAll("[data-routine-no-toggle]"));

      this.totalElement = root.querySelector("[data-routine-total]");
      this.shippingElement = root.querySelector("[data-routine-shipping]");
      this.button = root.querySelector("[data-routine-add]");
      this.buttonText = root.querySelector("[data-routine-button-text]");
      this.errorElement = root.querySelector("[data-routine-error]");

      this.variants = this.parseVariants();
      this.currentVariantId = Number(root.dataset.currentVariantId || 0);
      this.currentPrice = Number(root.dataset.currentPrice || 0);
      this.currentVariantAvailable = true;

      if (!this.button || !this.currentVariantId) return;

      this.bindEvents();
      this.syncCurrentVariant();
      this.update();
    }

    parseVariants() {
      const variantsElement = this.root.querySelector("[data-routine-variants]");

      if (!variantsElement) return [];

      try {
        return JSON.parse(variantsElement.textContent);
      } catch (error) {
        console.warn("[HC Complete Routine] Unable to parse variants.", error);
        return [];
      }
    }

    cssEscape(value) {
      if (window.CSS && typeof window.CSS.escape === "function") {
        return window.CSS.escape(value);
      }

      return String(value).replace(/[^a-zA-Z0-9_-]/g, "\\$&");
    }

    getProductScope() {
      return (
        this.root.closest("product-info") ||
        this.root.closest(".product") ||
        document
      );
    }

    getCurrentVariantInput() {
      const scope = this.getProductScope();

      const selectors = [
        this.sectionId
          ? `#product-form-${this.cssEscape(this.sectionId)} [name="id"]`
          : "",
        "form[action*='/cart/add'] [name='id']"
      ].filter(Boolean);

      for (const selector of selectors) {
        const input = scope.querySelector(selector);

        if (input && input.value) return input;
      }

      return null;
    }

    syncCurrentVariant() {
      const variantInput = this.getCurrentVariantInput();
      const variantId = Number(variantInput?.value || this.currentVariantId);

      if (!variantId) return;

      this.currentVariantId = variantId;

      const matchedVariant = this.variants.find((variant) => {
        return Number(variant.id) === variantId;
      });

      if (matchedVariant) {
        this.currentPrice = Number(matchedVariant.price || 0);
        this.currentVariantAvailable = matchedVariant.available !== false;
      }
    }

    bindEvents() {
      this.checkboxes.forEach((checkbox) => {
        checkbox.addEventListener("change", () => this.update());
      });

      this.noToggleElements.forEach((element) => {
        ["click", "mousedown", "pointerdown", "touchstart"].forEach((eventName) => {
          element.addEventListener(eventName, (event) => {
            event.stopPropagation();
          });
        });
      });

      this.items.forEach((item) => {
        item.addEventListener("click", (event) => {
          if (
            event.defaultPrevented ||
            event.target.closest("[data-routine-no-toggle]") ||
            event.target.closest("a") ||
            event.target.closest("input") ||
            event.target.closest("button") ||
            event.target.closest("label")
          ) {
            return;
          }

          const checkbox = item.querySelector("[data-routine-checkbox]");

          if (!checkbox || checkbox.disabled) return;

          checkbox.checked = !checkbox.checked;
          checkbox.dispatchEvent(new Event("change", { bubbles: true }));
        });
      });

      this.button.addEventListener("click", () => this.addToCart());

      const scope = this.getProductScope();

      scope.addEventListener("change", () => {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            this.syncCurrentVariant();
            this.update();
          });
        });
      });

      ["variant:change", "variant:changed", "product:variant-change"].forEach((eventName) => {
        document.addEventListener(eventName, () => {
          this.syncCurrentVariant();
          this.update();
        });
      });

      window.addEventListener("pageshow", () => {
        this.syncCurrentVariant();
        this.update();
      });
    }

    getSelectedAddOns() {
      return this.checkboxes
        .filter((checkbox) => checkbox.checked && !checkbox.disabled)
        .map((checkbox) => {
          const item = checkbox.closest("[data-routine-product]");

          return {
            id: Number(item?.dataset.variantId || 0),
            price: Number(item?.dataset.price || 0)
          };
        })
        .filter((item) => item.id > 0);
    }

    getSelectedItems() {
      const items = [
        {
          id: this.currentVariantId,
          quantity: 1
        },
        ...this.getSelectedAddOns().map((item) => ({
          id: item.id,
          quantity: 1
        }))
      ];

      return items.filter((item, index, collection) => {
        return collection.findIndex((candidate) => candidate.id === item.id) === index;
      });
    }

    calculateTotal() {
      return this.getSelectedAddOns().reduce((total, item) => {
        return total + item.price;
      }, this.currentPrice);
    }

    formatMoney(cents) {
      const currency =
        window.Shopify?.currency?.active ||
        document.documentElement.dataset.currency ||
        "USD";

      return new Intl.NumberFormat(document.documentElement.lang || "en-US", {
        style: "currency",
        currency
      }).format(cents / 100);
    }

    updateSelectedStyles() {
      this.checkboxes.forEach((checkbox) => {
        const item = checkbox.closest("[data-routine-product]");

        if (!item) return;

        item.classList.toggle("is-selected", checkbox.checked);
      });
    }

    update() {
      this.syncCurrentVariant();

      const selectedAddOns = this.getSelectedAddOns();
      const itemCount = 1 + selectedAddOns.length;
      const total = this.calculateTotal();
      const remaining = Math.max(this.freeShippingThreshold - total, 0);

      if (this.totalElement) {
        this.totalElement.textContent = this.formatMoney(total);
      }

      if (this.shippingElement) {
        this.shippingElement.classList.remove("is-qualified");

        if (this.freeShippingThreshold <= 0) {
          this.shippingElement.textContent = "";
        } else if (remaining === 0) {
          this.shippingElement.textContent = "✓ You qualify for free shipping";
          this.shippingElement.classList.add("is-qualified");
        } else {
          this.shippingElement.textContent =
            `Add ${this.formatMoney(remaining)} more for free shipping`;
        }
      }

      if (!this.currentVariantAvailable) {
        this.button.disabled = true;
        this.buttonText.textContent = "Current selection unavailable";
      } else {
        this.button.disabled = false;
        this.buttonText.textContent =
          itemCount === 1
            ? "Add current item to cart"
            : `Add ${itemCount} items to cart`;
      }

      this.updateSelectedStyles();
    }

    setLoading(isLoading) {
      this.button.disabled = isLoading || !this.currentVariantAvailable;

      if (isLoading) {
        this.buttonText.textContent = "Adding…";
      } else {
        this.update();
      }
    }

    showError(message = "") {
      if (!this.errorElement) return;

      this.errorElement.textContent = message;
      this.errorElement.hidden = !message;
    }

    async addToCart() {
      this.syncCurrentVariant();

      if (!this.currentVariantAvailable) {
        this.showError("The selected option is currently unavailable.");
        return;
      }

      this.showError();
      this.setLoading(true);

      try {
        const rootPath = window.Shopify?.routes?.root || "/";

        const response = await fetch(`${rootPath}cart/add.js`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json"
          },
          body: JSON.stringify({
            items: this.getSelectedItems()
          })
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(
            result.description ||
              result.message ||
              "Could not add the selected items."
          );
        }

        let cart = null;

        try {
          const cartResponse = await fetch(`${rootPath}cart.js`, {
            headers: {
              Accept: "application/json"
            }
          });

          if (cartResponse.ok) {
            cart = await cartResponse.json();
          }
        } catch (cartError) {
          console.warn("[HC Complete Routine] Cart refresh request failed.", cartError);
        }

        this.openCartDrawer(result, cart);
      } catch (error) {
        console.error("[HC Complete Routine]", error);

        this.showError(
          error.message || "Something went wrong. Please try again."
        );

        this.setLoading(false);
      }
    }

    openCartDrawer(result, cart) {
      const detail = {
        source: "hc-complete-routine",
        result,
        cart
      };

      ["cart:refresh", "cart:updated", "cart:open"].forEach((eventName) => {
        document.dispatchEvent(
          new CustomEvent(eventName, {
            bubbles: true,
            detail
          })
        );
      });

      document.dispatchEvent(
        new CustomEvent("hc:complete-routine-added", {
          bubbles: true,
          detail
        })
      );

      const cartDrawer = document.querySelector("cart-drawer");

      if (cartDrawer && typeof cartDrawer.open === "function") {
        cartDrawer.open();
        this.setLoading(false);
        return;
      }

      const cartTrigger =
        document.querySelector("[data-cart-drawer-open]") ||
        document.querySelector("[aria-controls='CartDrawer']") ||
        document.querySelector("#cart-icon-bubble");

      if (cartTrigger) {
        cartTrigger.click();
        this.setLoading(false);
        return;
      }

      window.location.assign(this.cartUrl);
    }
  }

  const initialize = (container = document) => {
    container.querySelectorAll(MODULE_SELECTOR).forEach((root) => {
      if (root.dataset.hcInitialized === "true") return;

      root.dataset.hcInitialized = "true";
      new HCCompleteRoutine(root);
    });
  };

  window.HCCompleteRoutineInitialize = initialize;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initialize());
  } else {
    initialize();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initialize(event.target);
  });
})();